import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { trackAppInit } from './utils/telemetry';
import { TelemetryListener } from './components/TelemetryListener';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SiteGate } from './components/SiteGate';
import { CookieConsentBanner } from './components/CookieConsent';
import { LoadingState } from './components/ui';

const Layout = lazy(() => import('./components/layout/Layout'));
const AppShell = lazy(() => import('./components/layout/AppShell'));
const Home = lazy(() => import('./pages/Home').then((module) => ({ default: module.Home })));
const Memberships = lazy(() => import('./pages/Memberships').then((module) => ({ default: module.Memberships })));
const Education = lazy(() => import('./pages/Education').then((module) => ({ default: module.Education })));
const Tools = lazy(() => import('./pages/Tools').then((module) => ({ default: module.Tools })));
const IndicatorDetail = lazy(() => import('./pages/IndicatorDetail').then((module) => ({ default: module.IndicatorDetail })));
const Research = lazy(() => import('./pages/Research').then((module) => ({ default: module.Research })));
const About = lazy(() => import('./pages/About').then((module) => ({ default: module.About })));
const Privacy = lazy(() => import('./pages/Privacy').then((module) => ({ default: module.Privacy })));
const Terms = lazy(() => import('./pages/Terms').then((module) => ({ default: module.Terms })));
const AppHome = lazy(() => import('./pages/app/AppHome').then((module) => ({ default: module.AppHome })));
const AppChart = lazy(() => import('./pages/app/Chart').then((module) => ({ default: module.AppChart })));
const AppJournal = lazy(() => import('./pages/app/Journal').then((module) => ({ default: module.AppJournal })));
const AppAnalytics = lazy(() => import('./pages/app/Analytics').then((module) => ({ default: module.AppAnalytics })));
const AppEducation = lazy(() => import('./pages/app/Education').then((module) => ({ default: module.AppEducation })));
const AppInsights = lazy(() => import('./pages/app/Insights').then((module) => ({ default: module.AppInsights })));
const AppSignals = lazy(() => import('./pages/app/Signals').then((module) => ({ default: module.AppSignals })));
const Forum = lazy(() => import('./pages/app/Forum').then((module) => ({ default: module.Forum })));
const ForumPost = lazy(() => import('./pages/app/ForumPost').then((module) => ({ default: module.ForumPostPage })));
const MarketContext = lazy(() => import('./pages/app/MarketContext').then((module) => ({ default: module.MarketContext })));
const AppCoach = lazy(() => import('./pages/app/Coach').then((module) => ({ default: module.AppCoach })));
const Pricing = lazy(() => import('./pages/app/Pricing').then((module) => ({ default: module.Pricing })));
const Settings = lazy(() => import('./pages/app/Settings').then((module) => ({ default: module.Settings })));
const AppReview = lazy(() => import('./pages/app/Review').then((module) => ({ default: module.AppReview })));
const AppChallenge = lazy(() => import('./pages/app/Challenge').then((module) => ({ default: module.AppChallenge })));
const AppAchievements = lazy(() => import('./pages/app/Achievements').then((module) => ({ default: module.AppAchievements })));
const Brokerage = lazy(() => import('./pages/app/Brokerage').then((module) => ({ default: module.Brokerage })));
const MockupIndex = lazy(() => import('./mockups').then((module) => ({ default: module.MockupIndex })));
const EducationDashboardMockup = lazy(() => import('./mockups/EducationDashboardMockup').then((module) => ({ default: module.EducationDashboardMockup })));
const DailyChallengeMockup = lazy(() => import('./mockups/DailyChallengeMockup').then((module) => ({ default: module.DailyChallengeMockup })));
const AITutorMockup = lazy(() => import('./mockups/AITutorMockup').then((module) => ({ default: module.AITutorMockup })));
const ReviewSessionMockup = lazy(() => import('./mockups/ReviewSessionMockup').then((module) => ({ default: module.ReviewSessionMockup })));
const AchievementsPageMockup = lazy(() => import('./mockups/AchievementsPageMockup').then((module) => ({ default: module.AchievementsPageMockup })));
const AuthCallback = lazy(() => import('./pages/auth').then((module) => ({ default: module.AuthCallback })));
const ResetPassword = lazy(() => import('./pages/auth').then((module) => ({ default: module.ResetPassword })));
const EvalDashboard = lazy(() => import('./pages/admin/EvalDashboard').then((module) => ({ default: module.EvalDashboard })));

const RouteFallback = () => (
  <div className="min-h-screen bg-st-dark px-4 py-10">
    <LoadingState
      message="Loading page..."
      variant="card"
      skeletonVariant="minimal"
      className="mx-auto max-w-3xl"
    />
  </div>
);

const App: React.FC = () => {
  useEffect(() => {
    trackAppInit();
  }, []);

  return (
    <SiteGate>
      <CookieConsentBanner />
      <ToastProvider>
        <AuthProvider>
          <ThemeProvider>
            <BrowserRouter>
              <TelemetryListener />
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                      {/* Auth callback routes - must be outside protected routes */}
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route path="/auth/reset-password" element={<ResetPassword />} />

                      {/* Mockups - Education Hub Design Prototypes */}
                      <Route path="/mockups" element={<MockupIndex />} />
                      <Route path="/mockups/dashboard" element={<EducationDashboardMockup />} />
                      <Route path="/mockups/challenge" element={<DailyChallengeMockup />} />
                      <Route path="/mockups/tutor" element={<AITutorMockup />} />
                      <Route path="/mockups/review" element={<ReviewSessionMockup />} />
                      <Route path="/mockups/achievements" element={<AchievementsPageMockup />} />

                      {/* StellarTrail App (Workflow product area) */}
                      <Route
                        path="/app"
                        element={
                          <ProtectedRoute>
                            <AppShell />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<AppHome />} />
                        <Route path="chart" element={<AppChart />} />
                        <Route path="journal" element={<AppJournal />} />
                        <Route path="analytics" element={<AppAnalytics />} />
                        <Route path="education" element={<AppEducation />} />
                        <Route path="education/review" element={<AppReview />} />
                        <Route path="education/challenge" element={<AppChallenge />} />
                        <Route path="education/achievements" element={<AppAchievements />} />
                        <Route path="market-context" element={<MarketContext />} />
                        <Route path="forum" element={<Forum />} />
                        <Route path="forum/:postId" element={<ForumPost />} />
                        <Route path="insights" element={<Navigate to="/app/market-context" replace />} />
                        <Route path="signals" element={<Navigate to="/app/market-context" replace />} />
                        <Route path="coach" element={<AppCoach />} />
                        <Route path="pricing" element={<Pricing />} />
                        <Route path="settings" element={<Settings />} />
                        <Route path="brokerage" element={<Brokerage />} />
                        <Route path="*" element={<Navigate to="/app" replace />} />
                      </Route>

                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute requiresAdmin>
                            <AppShell />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<Navigate to="/admin/eval" replace />} />
                        <Route path="eval" element={<EvalDashboard />} />
                      </Route>

                      {/* Public marketing site */}
                      <Route path="/" element={<Layout />}>
                        <Route index element={<Home />} />
                        <Route path="memberships" element={<Memberships />} />
                        <Route path="education" element={<Education />} />
                        <Route path="tools" element={<Tools />} />
                        <Route path="tools/:indicatorId" element={<IndicatorDetail />} />
                        <Route path="research" element={<Research />} />
                        <Route path="about" element={<About />} />
                        <Route path="privacy" element={<Privacy />} />
                        <Route path="terms" element={<Terms />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Route>
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ThemeProvider>
        </AuthProvider>
      </ToastProvider>
    </SiteGate>
  );
};

export default App;
